import {groupBy} from '@benchling/js-utils';

import {
  FRIENDLY_NAMES_BY_DOMAIN_TYPE,
  CONNECTOR_DOMAIN_COLOR,
  COLORS_BY_CHAIN_INDEX,
  DEEMPHASIZED_DOMAIN_OPACITY,
} from 'antibodies/AntibodyConstants';
import {
  AntibodyFormat,
  AntibodyFormatChain,
  AntibodyFormatDomain,
  DistinctChain,
  DistinctDomain,
  DistinctDomainWithChainIndex,
} from 'antibodies/AntibodyTypedefs';
import {assert} from 'Asserts';
import {AntibodyChainType, AntibodyDomainType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {hexToRgbObject} from 'ui-library/Colors';
import {capitalize} from 'util/NameUtil';

/**
 * Get a short display name for the associated entity field for this distinct domain.
 * Intended use is for inline labels, such as those in schema configuration or antibody viz.
 *
 * Keep in sync with get_antibody_domain_field_name in benchling/antibodies/format/domain/util.py
 */
export function distinctDomainToFieldName({type, specificity}: DistinctDomain): string {
  return `${type}${specificity ?? ''}`;
}

/**
 * Get a short display name for the associated entity field for this distinct chain.
 * Intended use is for inline labels, such as those in schema configuration or antibody viz.
 *
 * Keep in sync with get_antibody_chain_name in benchling/antibodies/format/domain/util.py
 */
export function distinctChainToFieldName({
  type,
  specificity,
}: Pick<DistinctChain, 'type' | 'specificity'>): string {
  const specificitySuffix = specificity != null ? `-${specificity}` : '';
  return `${capitalize(type)}${specificitySuffix}`;
}

/**
 * Get a long display name for the associated entity field for this distinct domain.
 * Intended use is for displaying above an input where one can view and/or set an entity for this field.
 */
export function distinctDomainToFieldLabel({type, specificity}: DistinctDomain): string {
  let friendlyName = FRIENDLY_NAMES_BY_DOMAIN_TYPE[type];
  let abbreviation = `${type}`;
  if (specificity != null) {
    friendlyName = `${friendlyName} ${specificity}`;
    abbreviation = `${abbreviation}${specificity}`;
  }
  return `${friendlyName} (${abbreviation})`;
}

/**
 * Get a stringy key for this distinct domain.
 * Intended use is for managing the React render cycle; i.e. passing as a `key` prop.
 */
export function distinctDomainToKey({type, specificity}: DistinctDomain): string {
  return specificity != null ? `${type}-${specificity}` : `${type}`;
}

function isConnectorDomain(domainType: AntibodyDomainType): boolean {
  return [AntibodyDomainType.H, AntibodyDomainType.J, AntibodyDomainType.L].includes(domainType);
}

export function isConstantDomain(domainType: AntibodyDomainType): boolean {
  return [
    AntibodyDomainType.CA,
    AntibodyDomainType.CB,
    AntibodyDomainType.CD,
    AntibodyDomainType.CG,
    AntibodyDomainType.CH1,
    AntibodyDomainType.CH2,
    AntibodyDomainType.CH3,
    AntibodyDomainType.CH4,
    AntibodyDomainType.CL,
  ].includes(domainType);
}

function isVariableDomain(domainType: AntibodyDomainType): boolean {
  return [
    AntibodyDomainType.VA,
    AntibodyDomainType.VB,
    AntibodyDomainType.VD,
    AntibodyDomainType.VG,
    AntibodyDomainType.VH,
    AntibodyDomainType.VHH,
    AntibodyDomainType.VL,
  ].includes(domainType);
}

function blendWithWhite(colorValue: number, opacity: number): string {
  return (colorValue + (255 - colorValue) * opacity).toFixed(1);
}

/**
 * Deemphasize a color by blending with white. Better than opacity because we have overlapping domains.
 * Adapted from https://stackoverflow.com/a/12228643
 */
function deemphasizeColor(color: string, opacity: number = DEEMPHASIZED_DOMAIN_OPACITY): string {
  const {r, g, b} = hexToRgbObject(color);
  return `rgb(${blendWithWhite(r, opacity)}, ${blendWithWhite(g, opacity)}, ${blendWithWhite(b, opacity)})`;
}

export function domainColor({
  distinctDomain,
  deemphasized,
  selected,
}: {
  distinctDomain: DistinctDomainWithChainIndex;
  deemphasized?: boolean;
  selected?: boolean;
}): {fill: string; stroke: string} {
  // Connectors don't have fill - they're just `path`s.
  if (isConnectorDomain(distinctDomain.type)) {
    // Note that there is no special color for selected connectors.
    const stroke = deemphasized ? deemphasizeColor(CONNECTOR_DOMAIN_COLOR) : CONNECTOR_DOMAIN_COLOR;
    return {fill: 'transparent', stroke};
  } else {
    assert(
      distinctDomain.chainIndex >= 0 && distinctDomain.chainIndex < COLORS_BY_CHAIN_INDEX.length,
      `Not enough colors configured for chain index ${distinctDomain.chainIndex}`
    );
    const chainColorSet = COLORS_BY_CHAIN_INDEX[distinctDomain.chainIndex];
    let baseFill: string;
    if (isVariableDomain(distinctDomain.type)) {
      baseFill = chainColorSet.variableDomainColor;
    } else if (isConstantDomain(distinctDomain.type)) {
      baseFill = chainColorSet.constantDomainColor;
    } else {
      assert(false, `Invalid domain type: ${distinctDomain.type}`);
    }
    const fill = deemphasized ? deemphasizeColor(baseFill) : baseFill;
    const stroke = selected ? chainColorSet.chainColor : 'transparent';
    return {fill, stroke};
  }
}

export function chainColor(chainIndex: number): string {
  assert(chainIndex >= 0 && chainIndex < COLORS_BY_CHAIN_INDEX.length, `Invalid chain index: ${chainIndex}`);
  return COLORS_BY_CHAIN_INDEX[chainIndex].chainColor;
}

/**
 * Maps each antibody domain ref to its corresponding domain.
 */
export function formatToDomainsByRef(
  format: Pick<AntibodyFormat, 'domains'>
): Record<number, AntibodyFormatDomain> {
  return format.domains.reduce<Record<number, AntibodyFormatDomain>>((acc, domain) => {
    acc[domain.ref] = domain;
    return acc;
  }, {});
}

/**
 * Key for grouping chains by distinct domains.
 */
function distinctChainGroupingKey(
  chain: AntibodyFormatChain,
  domainRefToDistinctDomain: Record<number, DistinctDomain>
): string {
  const distinctDomains = chain.domainRefs.map((ref) => {
    const distinctDomain = domainRefToDistinctDomain[ref];
    assert(distinctDomain != null, `Domain ref ${ref} not found`);
    return distinctDomain;
  });
  return JSON.stringify(distinctDomains);
}

/**
 * Helper function to generate chain labels with proper indexing
 */
function getAntibodyChainLabel(type: string, chainIndex: number | null): string {
  // If a chain index is provided, 1-index it and add it to the label.
  const indexSuffix = chainIndex != null ? ` ${chainIndex + 1}` : '';
  return `${capitalize(type)}${indexSuffix}`;
}

/**
 * Builds a list of all distinct chains in the format grouped by distinct domains. Preserves the order of the
 * chains in the format.
 *
 * Keep in sync with get_ordered_distinct_chains in benchling/antibodies/format/domain/util.py
 */
export function formatToSortedDistinctChains(
  format: Pick<AntibodyFormat, 'chains' | 'domains'>
): Array<DistinctChain> {
  const domainRefToDistinctDomain = format.domains.reduce<Record<number, DistinctDomain>>((acc, domain) => {
    acc[domain.ref] = {type: domain.type, specificity: domain.specificity};
    return acc;
  }, {});

  // Group chains by their distinct domains
  const chainsByDistinctDomains = groupBy(format.chains, (chain) =>
    distinctChainGroupingKey(chain, domainRefToDistinctDomain)
  );
  const seenChainKeys: Set<string> = new Set();

  const distinctChainsWithTypeIndex: Array<Omit<DistinctChain, 'label'> & {typeIndex: number}> = [];
  const chainTypeIndexes: Record<AntibodyChainType, number> = {
    [AntibodyChainType.ALPHA]: 0,
    [AntibodyChainType.BETA]: 0,
    [AntibodyChainType.HEAVY]: 0,
    [AntibodyChainType.LIGHT]: 0,
    [AntibodyChainType.SINGLE]: 0,
  };

  format.chains.forEach((chain) => {
    const key = distinctChainGroupingKey(chain, domainRefToDistinctDomain);
    if (seenChainKeys.has(key)) {
      return;
    }
    seenChainKeys.add(key);
    const groupedChains = chainsByDistinctDomains[key];

    // All chains in a group should have the same type and specificity
    const chainTypes = new Set(groupedChains.map((chain) => chain.type));
    const chainSpecificities = new Set(groupedChains.map((chain) => chain.specificity));

    if (chainTypes.size !== 1 || chainSpecificities.size !== 1) {
      throw new Error('Expected all chains in a group to have the same type and specificity');
    }

    const chainType = groupedChains[0].type;
    const chainSpecificity = groupedChains[0].specificity;

    distinctChainsWithTypeIndex.push({
      type: chainType,
      specificity: chainSpecificity,
      typeIndex: chainTypeIndexes[chainType],
      refs: groupedChains.map((chain) => chain.ref),
      domainRefsPerChain: groupedChains.map((chain) => chain.domainRefs),
    });

    chainTypeIndexes[chainType] += 1;
  });

  // Now set the labels based on the final counts
  const distinctChains = distinctChainsWithTypeIndex.map((chain) => ({
    type: chain.type,
    specificity: chain.specificity,
    refs: chain.refs,
    domainRefsPerChain: chain.domainRefsPerChain,
    // Chains should be labeled with an index only if needed to disambiguate among chains of the same type.
    label: getAntibodyChainLabel(chain.type, chainTypeIndexes[chain.type] > 1 ? chain.typeIndex : null),
  }));

  return distinctChains;
}

/**
 * Builds a list of all distinct (domain type, specificity) pairs in the format. Preserves the order of the
 * domains in the format.
 */
export function formatToSortedDistinctDomains(
  format: Pick<AntibodyFormat, 'domains'>
): Array<DistinctDomain> {
  const distinctDomains: Array<DistinctDomain> = [];
  for (const domain of format.domains) {
    // O(nlogn), yuck, but we should (TM) only be dealing with O(100) domains at a time. Python lets you use
    // a set for tuples of primitives!!!
    const alreadyFound = distinctDomains.find(
      (other) => other.type === domain.type && other.specificity === domain.specificity
    );
    if (!alreadyFound) {
      distinctDomains.push({type: domain.type, specificity: domain.specificity});
    }
  }
  return distinctDomains;
}

/**
 * Builds a list of all distinct (domain type, specificity) pairs in the format, together with index of the
 * chain that the domain belongs to (for visualization). Preserves the order of the domains in the format.
 */
export function formatToSortedDistinctDomainsWithChainIndex(
  format: Pick<AntibodyFormat, 'domains' | 'chains'>
): Array<DistinctDomainWithChainIndex> {
  const domainRefToChainIndex = formatToSortedDistinctChains(format).reduce<Record<number, number>>(
    (acc, chain, idx) => {
      for (const domainRef of chain.domainRefsPerChain.flat()) {
        acc[domainRef] = idx;
      }
      return acc;
    },
    {}
  );
  const distinctDomains: Array<DistinctDomainWithChainIndex> = [];
  for (const domain of format.domains) {
    const chainIndex = domainRefToChainIndex[domain.ref];
    // O(nlogn), yuck, but we should (TM) only be dealing with O(100) domains at a time. Python lets you use
    // a set for tuples of primitives!!!
    const alreadyFound = distinctDomains.find(
      (other) => other.type === domain.type && other.specificity === domain.specificity
    );
    if (!alreadyFound) {
      assert(chainIndex != null, `Domain ref ${domain.ref} not found in chains`);
      distinctDomains.push({type: domain.type, specificity: domain.specificity, chainIndex});
    } else {
      assert(alreadyFound.chainIndex === chainIndex, `Domain ref ${domain.ref} found in multiple chains`);
    }
  }
  return distinctDomains;
}
